import { ChangeEvent, useMemo, useState } from 'react';

import { AdminEventUsersQuery } from '../../apollo/__generated__/graphql';
import { useAdminEventUsersQuery } from '../../event/hooks/useAdminEventUsersQuery';
import { useCurrentEventQuery } from '../../event/hooks/useCurrentEventQuery';
import { useEventOptionsQuery } from '../../event/hooks/useEventOptionsQuery';
import { useStaffLeaveEventMutation } from '../../event/hooks/useStaffLeaveEventMutation';
import { useUpdateCaution } from '../../event/hooks/useUpdateCaution';
import { useUpdatePayment } from '../../event/hooks/useUpdatePayment';
import { useUpdateValidation } from '../../event/hooks/useUpdateValidation';
import { downloadUsersAsCsv } from '../../user/services/downloadUsersAsCsv.service';

export interface DialogType {
  title: string;
  description: string;
  onSubmit: () => void;
}

interface CommentDialogInformation {
  userId: string;
  eventId: string;
  orgaComment: string;
}

export type CheckboxFilterType = 'only' | 'only-not' | 'all';

const nextFilterByFilterType: Record<CheckboxFilterType, CheckboxFilterType> = {
  only: 'only-not',
  'only-not': 'all',
  all: 'only',
};

export const useAdminEventUsers = () => {
  const [search, setSearch] = useState<string>('');
  const [selectedPreviousEventId, setSelectedPreviousEventId] = useState<
    string | null
  >(null);
  const [cautionFilter, setCautionFilter] = useState<CheckboxFilterType>('all');
  const [paiementFilter, setPaiementFilter] =
    useState<CheckboxFilterType>('all');
  const [validationFilter, setValidationFilter] =
    useState<CheckboxFilterType>('all');
  const [participantToDeleteId, setParticipantToDeleteId] = useState<
    string | null
  >(null);

  const [commentDialogInformation, setCommentDialogInformation] =
    useState<CommentDialogInformation | null>(null);
  const [superLikeDialogInformation, setSuperLikeDialogInformation] = useState<{
    userId: string;
    eventId: string;
    maxSuperLikes: number;
  } | null>(null);
  const [dialog, setDialog] = useState<DialogType | null>(null);
  const { data: currentEventData } = useCurrentEventQuery();
  const { data: eventOptionsData, loading: isEventOptionsLoading } =
    useEventOptionsQuery();

  const { staffLeaveEvent } = useStaffLeaveEventMutation();
  const { data: eventUsersData, loading } = useAdminEventUsersQuery(
    currentEventData?.currentEvent?.id ?? null,
    selectedPreviousEventId,
  );
  const eventOptions = useMemo(
    () =>
      (eventOptionsData?.events ?? []).filter(
        (event) => event.id !== currentEventData?.currentEvent?.id,
      ),
    [eventOptionsData, currentEventData?.currentEvent?.id],
  );

  const { onCautionPress } = useUpdateCaution(setDialog);
  const { onClosePaymentDialog, onPaymentPress, paymentDialogInformation } =
    useUpdatePayment(setDialog);
  const { onValidatePress } = useUpdateValidation(setDialog);

  const eventUsers = useMemo(
    () =>
      (eventUsersData?.eventUsers ?? [])
        .filter((user) => {
          if (cautionFilter === 'all') {
            return true;
          }

          if (cautionFilter === 'only') {
            return user.hasCaution;
          }

          return !user.hasCaution;
        })
        .filter((user) => {
          if (paiementFilter === 'all') {
            return true;
          }

          if (paiementFilter === 'only') {
            return user.hasPaidTicket;
          }

          return !user.hasPaidTicket;
        })
        .filter((user) => {
          if (validationFilter === 'all') {
            return true;
          }

          if (validationFilter === 'only') {
            return user.isValidated;
          }

          return !user.isValidated;
        })
        .filter((eventUser) =>
          `${eventUser.firstname} ${eventUser.lastname}. ${eventUser.nickname} ${eventUser.studyTown} ${eventUser.studies}`
            .toLowerCase()
            .includes(search.toLowerCase()),
        ),
    [eventUsersData, validationFilter, cautionFilter, paiementFilter, search],
  );

  const onCloseDialog = () => {
    setDialog(null);
  };

  const onEditOrgaComment = (
    user: AdminEventUsersQuery['eventUsers'][number],
  ) => {
    const eventId = currentEventData?.currentEvent?.id ?? null;
    if (!eventId) {
      return;
    }
    setCommentDialogInformation({
      userId: user.id,
      eventId,
      orgaComment: user.orgaComment ?? '',
    });
  };

  const onCloseCommentDialog = () => {
    setCommentDialogInformation(null);
  };

  const onEditMaxSuperLikes = (
    user: AdminEventUsersQuery['eventUsers'][number],
  ) => {
    const eventId = currentEventData?.currentEvent?.id ?? null;
    if (!eventId) {
      return;
    }
    setSuperLikeDialogInformation({
      userId: user.id,
      eventId,
      maxSuperLikes: user.maxSuperLikes ?? 3,
    });
  };

  const onCloseSuperLikeDialog = () => {
    setSuperLikeDialogInformation(null);
  };

  const onShowDeleteParticipantDialog = (userId: string) => {
    setParticipantToDeleteId(userId);
  };
  const onCloseDeleteParticipantDialog = () => {
    setParticipantToDeleteId(null);
  };

  const onDeleteParticipant = () => {
    if (participantToDeleteId) {
      staffLeaveEvent({
        eventId: currentEventData?.currentEvent?.id ?? '',
        userId: participantToDeleteId,
      });
      onCloseDeleteParticipantDialog();
    }
  };

  const onDownloadCsv = () => {
    downloadUsersAsCsv(eventUsers ?? []);
  };

  const onCautionFilterCheckboxChange = () => {
    setCautionFilter(nextFilterByFilterType[cautionFilter]);
  };

  const onPaiementFilterCheckboxChange = () => {
    setPaiementFilter(nextFilterByFilterType[paiementFilter]);
  };

  const onValidationFilterCheckboxChange = () => {
    setValidationFilter(nextFilterByFilterType[validationFilter]);
  };

  const clearSearch = () => {
    setSearch('');
  };

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const onSelectedPreviousEventIdChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setSelectedPreviousEventId(event.target.value || null);
  };

  return {
    eventUsers,
    loading: loading || isEventOptionsLoading,
    eventOptions,
    onCautionPress,
    dialog,
    onCloseDialog,
    onPaymentPress,
    paymentDialogInformation,
    commentDialogInformation,
    onClosePaymentDialog,
    onCloseCommentDialog,
    superLikeDialogInformation,
    onEditMaxSuperLikes,
    onCloseSuperLikeDialog,
    onValidatePress,
    onEditOrgaComment,
    onCloseDeleteParticipantDialog,
    onDeleteParticipant,
    participantToDeleteId,
    onShowDeleteParticipantDialog,
    onDownloadCsv,
    paiementFilter,
    onPaiementFilterCheckboxChange,
    onCautionFilterCheckboxChange,
    cautionFilter,
    onValidationFilterCheckboxChange,
    validationFilter,
    clearSearch,
    onSearchChange,
    search,
    selectedPreviousEventId: selectedPreviousEventId ?? '',
    onSelectedPreviousEventIdChange,
  };
};
